import { ChevronDown, ChevronRight, Download, Loader2, ThumbsDown, ThumbsUp } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  adminClient,
  type AdminUsageEventRow,
  type AdminUsageSummary,
} from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { TablePager } from '@/components/table/TablePager';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LoadError } from '@/components/ui/load-state';
import {
  chartAxisTick,
  chartGridStroke,
  chartLineCursor,
  chartSeriesColor,
  chartTooltipProps,
} from '@/lib/chartTheme';
import {
  ACTIVITY_PAGE_SIZE,
  formatCount,
  formatDate,
  formatDateTime,
  formatUsd,
  isoToDateInput,
  rangeForDays,
  rangeForLifecycle,
  rangeFromCustomDates,
} from '@/lib/format';
import { runStatusLabel, runStatusTone } from '@/lib/runTelemetry';

const PERIODS = [7, 30, 90] as const;
type WindowPeriod = (typeof PERIODS)[number] | 'custom';
type PeriodChoice = WindowPeriod | 'lifecycle';

/** The organization view opens on the same 30 days the org Overview headlines. */
export const ORG_SPEND_DAYS = 30;

export function spendLabel(period: PeriodChoice): string {
  if (period === 'lifecycle') return 'Spend, whole project';
  if (period === 'custom') return 'Spend, selected dates';
  return `Spend, last ${period} days`;
}

/** The one spend figure, split the same way everywhere it is shown. */
export function spendBreakdown(summary: AdminUsageSummary): { total: number; aiBuilding: number; integrations: number } {
  const aiBuilding = summary.totals.llmCostUsd ?? summary.totals.costUsd;
  const integrations = summary.totals.integrationCostUsd ?? 0;
  return { total: aiBuilding + integrations, aiBuilding, integrations };
}

const seriesLabel = (name: unknown) => (name === 'integrationCostUsd' ? 'Integrations' : 'AI building');

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border p-4">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function LoadingRow({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      {label}
    </div>
  );
}

function resolveRange(
  period: PeriodChoice,
  customFrom: string,
  customTo: string,
  lifecycleFrom: string | undefined,
): { from: string; to: string } | undefined {
  if (period === 'lifecycle') return rangeForLifecycle(lifecycleFrom);
  if (period === 'custom') return rangeFromCustomDates(customFrom, customTo);
  return rangeForDays(period);
}

export function OrgUsagePanel({
  orgId,
  projectId,
  lifecycleFrom,
  emptyActivityLabel = 'No activity in this range',
}: {
  orgId: string;
  projectId?: string;
  /** When set (project detail), default to full lifecycle instead of 7/30/90. */
  lifecycleFrom?: string;
  emptyActivityLabel?: string;
}) {
  const lifecycleMode = Boolean(lifecycleFrom || projectId);
  const defaults = lifecycleMode
    ? rangeForLifecycle(lifecycleFrom)
    : rangeForDays(ORG_SPEND_DAYS);
  const [period, setPeriod] = useState<PeriodChoice>(lifecycleMode ? 'lifecycle' : ORG_SPEND_DAYS);
  const [customFrom, setCustomFrom] = useState(isoToDateInput(defaults.from));
  const [customTo, setCustomTo] = useState(isoToDateInput(defaults.to));
  const [customOpen, setCustomOpen] = useState(false);
  const [summary, setSummary] = useState<AdminUsageSummary | undefined>();
  const [events, setEvents] = useState<AdminUsageEventRow[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [cursorStack, setCursorStack] = useState<Array<string | undefined>>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | undefined>();
  const [reloadKey, setReloadKey] = useState(0);

  const activeRange = useMemo(
    () => resolveRange(period, customFrom, customTo, lifecycleFrom),
    [period, customFrom, customTo, lifecycleFrom],
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!activeRange) {
        if (period === 'custom') {
          setLoading(false);
          return;
        }
      }
      const range = activeRange;
      if (!range) return;
      setLoading(true);
      setLoadError(undefined);
      setCursorStack([undefined]);
      setPageIndex(0);
      try {
        const summaryPromise = projectId
          ? adminClient.getProjectUsageSummary(orgId, projectId, range)
          : adminClient.getOrgUsageSummary(orgId, { ...range, projectId });
        const eventsPromise = projectId
          ? adminClient.getProjectUsageEvents(orgId, projectId, {
              ...range,
              limit: ACTIVITY_PAGE_SIZE,
            })
          : adminClient.getOrgUsageEvents(orgId, {
              ...range,
              limit: ACTIVITY_PAGE_SIZE,
              projectId,
            });
        const [nextSummary, nextEvents] = await Promise.all([summaryPromise, eventsPromise]);
        if (!cancelled) {
          setSummary(nextSummary);
          setEvents(nextEvents.events);
          setNextCursor(nextEvents.nextCursor);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load usage';
          setLoadError(message);
          toast.error(message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, projectId, activeRange, period, lifecycleFrom, reloadKey]);

  const loadEventsPage = async (cursor: string | undefined, nextIndex: number, stack: Array<string | undefined>) => {
    if (!activeRange) return;
    setEventsLoading(true);
    try {
      const nextEvents = projectId
        ? await adminClient.getProjectUsageEvents(orgId, projectId, {
            ...activeRange,
            limit: ACTIVITY_PAGE_SIZE,
            cursor,
          })
        : await adminClient.getOrgUsageEvents(orgId, {
            ...activeRange,
            limit: ACTIVITY_PAGE_SIZE,
            cursor,
            projectId,
          });
      setEvents(nextEvents.events);
      setNextCursor(nextEvents.nextCursor);
      setCursorStack(stack);
      setPageIndex(nextIndex);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load activity page');
    } finally {
      setEventsLoading(false);
    }
  };


  const [downloading, setDownloading] = useState(false);

  const downloadActivity = async () => {
    if (!activeRange) return;
    setDownloading(true);
    try {
      const response = projectId
        ? await adminClient.downloadProjectUsageActivityCsv(orgId, projectId, activeRange)
        : await adminClient.downloadOrgUsageActivityCsv(orgId, {
            ...activeRange,
            projectId,
          });
      const truncated = response.headers.get('X-Studio-Export-Truncated') === 'true';
      const maxRows = response.headers.get('X-Studio-Export-Max-Rows') || '5000';
      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = /filename="?([^";]+)"?/i.exec(disposition);
      const filename = match?.[1] || 'studio-usage-activity.csv';
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      if (truncated) {
        toast.message(`Export capped at ${maxRows} rows for this range`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to download activity CSV');
    } finally {
      setDownloading(false);
    }
  };

  const chartData = useMemo(
    () =>
      (summary?.series || []).map((point) => ({
        dateLabel: point.date.slice(5),
        llmCostUsd: point.llmCostUsd ?? point.costUsd ?? 0,
        integrationCostUsd: point.integrationCostUsd ?? 0,
      })),
    [summary],
  );

  const selectLifecycle = () => {
    setPeriod('lifecycle');
    setCustomOpen(false);
  };

  const selectCustom = () => {
    if (lifecycleMode && period === 'lifecycle' && activeRange) {
      setCustomFrom(isoToDateInput(activeRange.from));
      setCustomTo(isoToDateInput(activeRange.to));
    }
    setPeriod('custom');
    setCustomOpen(true);
  };

  if (period === 'custom' && !activeRange) {
    return (
      <div className="flex flex-col gap-6">
        <RangeControls
          period={period}
          setPeriod={setPeriod}
          customFrom={customFrom}
          customTo={customTo}
          setCustomFrom={setCustomFrom}
          setCustomTo={setCustomTo}
          projectId={projectId}
          lifecycleMode={lifecycleMode}
          lifecycleFrom={lifecycleFrom}
          customOpen={customOpen}
          setCustomOpen={setCustomOpen}
          onSelectLifecycle={selectLifecycle}
          onSelectCustom={selectCustom}
          activeRange={undefined}
        />
        <p className="text-sm text-muted-foreground">Choose a valid from → to date range.</p>
      </div>
    );
  }

  if (loading) return <LoadingRow label="Loading usage" />;
  if (!summary && loadError) {
    return (
      <LoadError
        message={loadError}
        onRetry={() => setReloadKey((key) => key + 1)}
        className="rounded-xl border border-border"
      />
    );
  }
  if (!summary) return null;

  const spend = spendBreakdown(summary);
  const isLifecycleView = period === 'lifecycle';
  const people = summary.totals.activeUsers;

  return (
    <div className="flex flex-col gap-6">
      <RangeControls
        period={period}
        setPeriod={setPeriod}
        customFrom={customFrom}
        customTo={customTo}
        setCustomFrom={setCustomFrom}
        setCustomTo={setCustomTo}
        projectId={projectId}
        lifecycleMode={lifecycleMode}
        lifecycleFrom={lifecycleFrom}
        customOpen={customOpen}
        setCustomOpen={setCustomOpen}
        onSelectLifecycle={selectLifecycle}
        onSelectCustom={selectCustom}
        activeRange={activeRange}
      />

      <div className="grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <div className="rounded-xl border border-border p-5">
          <p className="text-xs font-medium text-muted-foreground">{spendLabel(period)}</p>
          <p className="mt-1 text-3xl font-semibold tabular-nums">{formatUsd(spend.total)}</p>
          <dl className="mt-4 grid grid-cols-2 gap-3 border-t border-border pt-4 text-sm">
            <div>
              <dt className="text-xs text-muted-foreground">AI building</dt>
              <dd className="font-medium tabular-nums">{formatUsd(spend.aiBuilding)}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Integrations</dt>
              <dd className="font-medium tabular-nums">{formatUsd(spend.integrations)}</dd>
            </div>
          </dl>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <Stat label="Builds" value={formatCount(summary.totals.runs)} />
          <Stat label={people === 1 ? 'Person building' : 'People building'} value={formatCount(people)} />
          <Stat
            label="Tokens in / out"
            value={`${formatCount(summary.totals.inputTokens)} / ${formatCount(summary.totals.outputTokens)}`}
          />
        </div>
      </div>

      <div className="h-64 rounded-xl border border-border p-4">
        <p className="mb-2 text-sm font-semibold text-foreground">Spend over time</p>
        <ResponsiveContainer width="100%" height="90%">
          <AreaChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
            <XAxis dataKey="dateLabel" tick={chartAxisTick} />
            <YAxis
              tick={chartAxisTick}
              tickFormatter={(value: number) => formatUsd(value)}
              width={64}
            />
            <Tooltip
              {...chartTooltipProps}
              cursor={chartLineCursor}
              formatter={(value, name) => [formatUsd(Number(value)), seriesLabel(name)]}
            />
            <Legend formatter={seriesLabel} />
            <Area
              type="monotone"
              dataKey="llmCostUsd"
              name="llmCostUsd"
              stroke={chartSeriesColor(0)}
              fill={chartSeriesColor(0)}
              fillOpacity={0.18}
              strokeWidth={2}
            />
            <Area
              type="monotone"
              dataKey="integrationCostUsd"
              name="integrationCostUsd"
              stroke={chartSeriesColor(2)}
              fill={chartSeriesColor(2)}
              fillOpacity={0.14}
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <section className="overflow-hidden rounded-xl border border-border">
        <div className="border-b border-border px-4 py-3">
          <h3 className="text-sm font-semibold text-foreground">By person</h3>
          <p className="text-xs text-muted-foreground">
            {isLifecycleView
              ? 'People who built across the project lifecycle'
              : 'People who built in this range'}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border bg-muted/40 text-xs font-medium text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Person</th>
                <th className="px-4 py-3">Builds</th>
                <th className="px-4 py-3">Spend</th>
              </tr>
            </thead>
            <tbody>
              {summary.byUser.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-10 text-center text-muted-foreground">
                    No one built in this range
                  </td>
                </tr>
              ) : (
                summary.byUser.map((row) => (
                  <tr
                    key={row.userId}
                    className="border-b border-border last:border-0 hover:bg-muted/40"
                  >
                    <td className="px-4 py-3">
                      <p>{row.displayName || row.email || 'Unknown user'}</p>
                      {row.email && row.displayName && (
                        <p className="text-xs text-muted-foreground">{row.email}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 tabular-nums">{formatCount(row.runs)}</td>
                    <td className="px-4 py-3 tabular-nums">{formatUsd(row.costUsd)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="overflow-hidden rounded-xl border border-border">
        <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-foreground">Activity</h3>
            <p className="text-xs text-muted-foreground">
              One row per build (integration spend rolls up per session) · open a build for its
              latency telemetry · page {pageIndex + 1}
              {isLifecycleView ? ' · full lifecycle' : ''}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 rounded-full"
            disabled={downloading || !activeRange || eventsLoading}
            onClick={() => void downloadActivity()}
          >
            {downloading ? (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            ) : (
              <Download className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
            )}
            Download activity
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border bg-muted/40 text-xs font-medium text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Event</th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Feedback</th>
                <th className="px-4 py-3">Spend</th>
                <th className="px-4 py-3">When</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">
                    {emptyActivityLabel}
                  </td>
                </tr>
              ) : (
                events.map((event) => (
                  <tr
                    key={`${event.kind}:${event.runId}`}
                    className="border-b border-border last:border-0 hover:bg-muted/40"
                  >
                    <td className="px-4 py-3">
                      <div className="min-w-0">
                        <p className="flex flex-wrap items-center gap-2 font-medium text-foreground">
                          {event.kind === 'integration' ? (
                            <span className="inline-flex rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                              Integration
                            </span>
                          ) : null}
                          {event.kind === 'llm' && event.runId ? (
                            <Link
                              to={`/organizations/${encodeURIComponent(orgId)}/runs/${encodeURIComponent(event.runId)}`}
                              className="hover:underline"
                            >
                              {event.title}
                            </Link>
                          ) : (
                            <span>{event.title}</span>
                          )}
                          {event.kind === 'llm' && event.status ? (
                            <StatusPill tone={runStatusTone(event.status)}>
                              {runStatusLabel(event.status)}
                            </StatusPill>
                          ) : null}
                        </p>
                        {event.promptSnippet ? (
                          <p className="mt-0.5 max-w-md truncate text-xs text-muted-foreground">
                            {event.promptSnippet}
                          </p>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {event.displayName || event.email || 'Unknown user'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {event.feedback && event.feedback.votes.length > 0 ? (
                        <div className="space-y-1">
                          <p className="flex items-center gap-2 text-xs tabular-nums">
                            <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                              <ThumbsUp className="h-3 w-3" aria-hidden="true" />
                              {event.feedback.upCount}
                            </span>
                            <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                              <ThumbsDown className="h-3 w-3" aria-hidden="true" />
                              {event.feedback.downCount}
                            </span>
                          </p>
                          <ul className="max-w-xs space-y-0.5 text-[11px] leading-4">
                            {event.feedback.votes.slice(0, 4).map((vote) => (
                              <li key={`${vote.userId}:${vote.runId}:${vote.updatedAt}`}>
                                <span className="font-medium text-foreground">
                                  {vote.displayName || vote.email || 'Unknown user'}
                                </span>
                                {': '}
                                {vote.rating === 'up' ? 'positive' : 'negative'}
                              </li>
                            ))}
                            {event.feedback.votes.length > 4 ? (
                              <li className="text-muted-foreground">
                                +{event.feedback.votes.length - 4} more
                              </li>
                            ) : null}
                          </ul>
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3 tabular-nums">
                      {typeof event.usage.costUsd === 'number'
                        ? formatUsd(event.usage.costUsd)
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {formatDateTime(event.createdAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <TablePager
          mode="cursor"
          pageSize={ACTIVITY_PAGE_SIZE}
          loadedCount={events.length}
          hasPrev={pageIndex > 0}
          hasNext={Boolean(nextCursor)}
          disabled={eventsLoading}
          onPrev={() => {
            const prevIndex = Math.max(0, pageIndex - 1);
            void loadEventsPage(cursorStack[prevIndex], prevIndex, cursorStack.slice(0, prevIndex + 1));
          }}
          onNext={() => {
            if (!nextCursor) return;
            const nextStack = [...cursorStack.slice(0, pageIndex + 1), nextCursor];
            void loadEventsPage(nextCursor, pageIndex + 1, nextStack);
          }}
        />
      </section>
    </div>
  );
}

function RangeControls({
  period,
  setPeriod,
  customFrom,
  customTo,
  setCustomFrom,
  setCustomTo,
  projectId,
  lifecycleMode,
  lifecycleFrom,
  customOpen,
  setCustomOpen,
  onSelectLifecycle,
  onSelectCustom,
  activeRange,
}: {
  period: PeriodChoice;
  setPeriod: (value: PeriodChoice) => void;
  customFrom: string;
  customTo: string;
  setCustomFrom: (value: string) => void;
  setCustomTo: (value: string) => void;
  projectId?: string;
  lifecycleMode: boolean;
  lifecycleFrom?: string;
  customOpen: boolean;
  setCustomOpen: (value: boolean) => void;
  onSelectLifecycle: () => void;
  onSelectCustom: () => void;
  activeRange?: { from: string; to: string };
}) {
  if (lifecycleMode) {
    const showCustom = period === 'custom' || customOpen;
    return (
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <p className="text-sm font-medium text-foreground">
              {period === 'lifecycle' ? 'Project lifecycle' : 'Custom range'}
            </p>
            <p className="text-sm text-muted-foreground">
              {period === 'lifecycle'
                ? `Investigation totals from ${formatDate(lifecycleFrom || activeRange?.from)} through now (createdAt → today).`
                : 'Forensic zoom inside this project. Headline metrics stay scoped to the custom window while active.'}
            </p>
          </div>
          <div className="flex flex-wrap gap-1">
            <Button
              type="button"
              size="sm"
              variant={period === 'lifecycle' ? 'default' : 'outline'}
              onClick={onSelectLifecycle}
            >
              Lifecycle
            </Button>
            <Button
              type="button"
              size="sm"
              variant={period === 'custom' ? 'default' : 'outline'}
              onClick={() => {
                if (period === 'custom') {
                  setCustomOpen(!customOpen);
                } else {
                  onSelectCustom();
                }
              }}
            >
              {showCustom && period === 'custom' ? (
                <ChevronDown className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <ChevronRight className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
              )}
              Custom range
            </Button>
          </div>
        </div>
        {period === 'custom' && showCustom ? (
          <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border p-4">
            <div>
              <Label htmlFor="usage-from">From</Label>
              <Input
                id="usage-from"
                className="mt-2"
                type="date"
                value={customFrom}
                onChange={(event) => setCustomFrom(event.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="usage-to">To</Label>
              <Input
                id="usage-to"
                className="mt-2"
                type="date"
                value={customTo}
                onChange={(event) => setCustomTo(event.target.value)}
              />
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {projectId
            ? 'Activity inside this project (builds, asks, costs, users).'
            : 'Everything this organization spent in Studio: AI building and integrations.'}
        </p>
        <div className="flex flex-wrap gap-1">
          {PERIODS.map((value) => (
            <Button
              key={value}
              type="button"
              size="sm"
              variant={period === value ? 'default' : 'outline'}
              onClick={() => setPeriod(value)}
            >
              {value}d
            </Button>
          ))}
          <Button
            type="button"
            size="sm"
            variant={period === 'custom' ? 'default' : 'outline'}
            onClick={() => setPeriod('custom')}
          >
            Custom
          </Button>
        </div>
      </div>
      {period === 'custom' ? (
        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border p-4">
          <div>
            <Label htmlFor="usage-from">From</Label>
            <Input
              id="usage-from"
              className="mt-2"
              type="date"
              value={customFrom}
              onChange={(event) => setCustomFrom(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="usage-to">To</Label>
            <Input
              id="usage-to"
              className="mt-2"
              type="date"
              value={customTo}
              onChange={(event) => setCustomTo(event.target.value)}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
